import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { useMutation } from '@tanstack/react-query'
import { MessageSquare, Mic, Video } from 'lucide-react'
import { useState } from 'react'

type Modality = 'video' | 'audio' | 'chat'

const MODALITY_OPTIONS: {
  readonly value: Modality
  readonly icon: React.ElementType
  readonly label: string
  readonly description: string
}[] = [
  {
    value: 'video',
    icon: Video,
    label: 'Video',
    description: 'Camera and microphone',
  },
  {
    value: 'audio',
    icon: Mic,
    label: 'Audio only',
    description: 'Microphone only',
  },
  {
    value: 'chat',
    icon: MessageSquare,
    label: 'Text / Chat',
    description: 'Typed responses',
  },
]

export const ModalityEditor = ({
  jobId,
  allowedModalities,
  isLocked,
}: {
  jobId: Id<'jobs'>
  allowedModalities: Modality[]
  isLocked: boolean
}) => {
  const [selected, setSelected] = useState<Modality[]>(allowedModalities)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const { mutateAsync: updateMyJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const toggleModality = (modality: Modality): void => {
    if (isLocked) return
    setSelected((prev) =>
      prev.includes(modality)
        ? prev.filter((m) => m !== modality)
        : [...prev, modality],
    )
    setSaved(false)
  }

  const handleSave = async (): Promise<void> => {
    if (selected.length === 0) return
    setSaving(true)
    await updateMyJob({ jobId, allowedModalities: selected })
    setSaving(false)
    setSaved(true)
  }

  const hasChanges =
    selected.length !== allowedModalities.length ||
    selected.some((m) => !allowedModalities.includes(m))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {MODALITY_OPTIONS.map(({ value, icon: Icon, label, description }) => (
          <label
            key={value}
            className={`flex items-center gap-4 rounded-lg border p-4 ${
              isLocked
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:border-primary/50'
            } ${selected.includes(value) ? 'border-primary bg-primary/5' : 'border-border'}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => toggleModality(value)}
              disabled={isLocked}
              className="sr-only"
            />
            <Icon className="size-5 shrink-0 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">{label}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
            <div
              className={`size-4 rounded border-2 flex items-center justify-center ${
                selected.includes(value)
                  ? 'border-primary bg-primary'
                  : 'border-muted-foreground'
              }`}
            >
              {selected.includes(value) && (
                <svg
                  className="size-2.5 text-primary-foreground"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1.5 5L4 7.5L8.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </label>
        ))}
      </div>

      {selected.length === 0 && (
        <p className="text-xs text-destructive">
          At least one modality must be selected.
        </p>
      )}

      {!isLocked && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || selected.length === 0 || !hasChanges}
          className="self-start px-4 py-2 text-sm bg-primary text-primary-foreground rounded disabled:opacity-50"
        >
          {saving ? 'Saving…' : saved && !hasChanges ? 'Saved' : 'Save'}
        </button>
      )}
    </div>
  )
}
